import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useApp } from '@/context/AppContext';
import LanguageSwitcher from './LanguageSwitcher';
import AdminNotificationBell from './AdminNotificationBell';
import GlobalSearch from './GlobalSearch';
import WorkspacePurposeBanner from './WorkspacePurposeBanner';

const ADMIN_ROLES = new Set(['hm_admin', 'hm_advisor']);
// Sesión H — evaluated role has NO global search and NO notifications bell.
const SEARCH_ENABLED_ROLES = new Set(['hm_admin', 'hm_advisor', 'client_admin', 'client_hr']);

const initials = (user) => {
  const name = (user?.name || '').trim();
  if (name) {
    const parts = name.split(/\s+/);
    return ((parts[0][0] || '') + (parts.length > 1 ? parts[1][0] : (parts[0][1] || ''))).toUpperCase();
  }
  return (user?.email || '?').slice(0, 2).toUpperCase();
};

export const Topbar = ({ title, subtitle, actions, leading }) => {
  const { user } = useApp();
  const { t } = useTranslation();
  const isAdmin = ADMIN_ROLES.has(user?.role);
  const canSearch = SEARCH_ENABLED_ROLES.has(user?.role);
  const isEvaluated = user?.role === 'evaluated';

  return (
    <header className="sticky top-0 z-20 bg-background/80 backdrop-blur-xl border-b border-border">
      <div className="flex items-center flex-wrap gap-x-4 gap-y-2 px-6 lg:px-8 py-4">
        {leading && <div className="shrink-0">{leading}</div>}
        <div className="flex-1 basis-[220px] min-w-0">
          {/* Founder UI fix 2026-06 — suite pages pass an inline-flex span
               (title + suite badge); force it to wrap inside its column so a
               long badge (e.g. "Evaluación por Competencias") never overlaps
               the global search/actions. Shared fix, no font-size changes. */}
          <h1 className="text-xl md:text-2xl font-display font-bold tracking-tight text-foreground leading-tight break-words [&>span]:flex-wrap [&>span]:max-w-full" title={typeof title === 'string' ? title : undefined}>{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground mt-0.5 break-words leading-snug">{subtitle}</p>}
        </div>

        {canSearch && <GlobalSearch />}

        <div className="flex items-center flex-wrap justify-end gap-2 ml-auto min-w-0 max-w-full">
          {actions}
          {/* evaluated pages render inside EvaluatedLayout, which already has
              its own language switcher in the top bar — avoid the duplicate */}
          {!isEvaluated && <LanguageSwitcher />}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" aria-label={t('topbar.help')} data-testid="topbar-help-btn" className="h-9 w-9 text-muted-foreground hover:text-foreground">
                <HelpCircle className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80" data-testid="topbar-help-popover">
              <p className="text-base font-semibold">{t('topbar.helpTitle')}</p>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                {t('topbar.helpBody')}{' '}
                <a href="mailto:info@hmadvis.com" className="text-primary font-medium hover:underline" data-testid="topbar-help-mailto">
                  info@hmadvis.com
                </a>.
              </p>
            </PopoverContent>
          </Popover>
          {isAdmin || (user?.role || '').startsWith('client') ? (
            <AdminNotificationBell />
          ) : isEvaluated ? null : (
            <Button variant="ghost" size="icon" aria-label={t('topbar.notifications')} className="h-9 w-9 relative text-muted-foreground hover:text-foreground">
              <Bell className="h-4 w-4" />
            </Button>
          )}
          {user && (
            <Avatar className="h-9 w-9 ml-1 border border-border cursor-pointer">
              <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                {initials(user)}
              </AvatarFallback>
            </Avatar>
          )}
        </div>
      </div>
      <WorkspacePurposeBanner />
    </header>
  );
};

export default Topbar;
